import {
  Button,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
  Group,
  MultiSelect,
  Select,
  Stack
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { IconCheck } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useGenerators } from "../../../../hooks/useGenerators";
import { useOperators } from "../../../../hooks/useOperators";
import { Abregler } from "../../../../types/api.types";
import { OperatorFeatures } from "../../../../urls";
import { LUXON_MANTINE_DATEFORMAT } from "../../../../utils/dates/dates.constants";
import { downloadAsExcel } from "../../../../utils/files/downloadAsExcel";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { useAusfallarbeitTimeSeries } from "../../../RevenueMonitoring/hooks/useAusfallarbeitTimeSeries";
import { AusfallarbeitChart } from "./AusfallarbeitChart";
import { AusfallarbeitTable } from "./AusfallarbeitTable";
import { AusfallSalesCard } from "./AusfallSalesCard";
import { buildAusfallarbeitExportTableData } from "./utils/buildAusfallarbeitExportTableData";

const ALL_GENERATORS_VALUE = "all";

// TODO: replace with contract validity period from backend once available // noqa: MISSING_TODO_LINK
const MIN_DATE = DateTime.fromISO("2016-01-01") as DateTime<true>;
const MAX_DATE = DateTime.now();

function getLastFullMonth(): [DateTime<true>, DateTime<true>] {
  const lastMonth = DateTime.now().minus({ months: 1 });
  return [lastMonth.startOf("month"), lastMonth.endOf("month")] as [
    DateTime<true>,
    DateTime<true>
  ];
}

function renderGeneratorOption(
  { option }: ComboboxLikeRenderOptionInput<ComboboxItem>,
  isAllSelected: boolean
) {
  return (
    <Group gap="xs" wrap="nowrap">
      {isAllSelected && <IconCheck size={14} style={{ flexShrink: 0 }} />}
      {option.label}
    </Group>
  );
}
function AusfallarbeitPage() {
  const [companyId, setCompanyId] = useState<number | undefined>();
  const [selectedGeneratorIds, setSelectedGeneratorIds] = useState<string[]>([
    ALL_GENERATORS_VALUE
  ]);
  const [dateRange, setDateRange] =
    useState<[DateTime<true>, DateTime<true> | null]>(getLastFullMonth);
  const { projectId } = useParams<{ projectId: string | undefined }>();

  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: companiesError
  } = useOperators({
    activeFeatures: [OperatorFeatures.RevenueMonitoring],
    project_id: projectId
  });

  const companyOptions = useMemo<ComboboxItem[]>(
    () =>
      companies?.map((c) => ({ label: c.name, value: c.id.toString() })) ?? [],
    [companies]
  );

  useEffect(() => {
    if (!companyId && companyOptions.length > 0) {
      setCompanyId(Number(companyOptions[0].value));
    }
  }, [companyOptions, companyId]);

  const {
    data: generators,
    isLoading: isLoadingGenerators,
    error: generatorsError
  } = useGenerators({ personId: companyId });

  const generatorOptions = useMemo<ComboboxItem[]>(
    () => [
      { label: "Alle Anlagen", value: ALL_GENERATORS_VALUE },
      ...(generators?.map((generator) => ({
        label: generator.trId
          ? `${generator.name} (${generator.trId})`
          : generator.name,
        value: generator.id.toString()
      })) ?? [])
    ],
    [generators]
  );

  const numericGeneratorIds = useMemo(() => {
    if (selectedGeneratorIds.includes(ALL_GENERATORS_VALUE)) {
      return generators?.map((g) => g.id) ?? [];
    }
    return selectedGeneratorIds.map(Number);
  }, [selectedGeneratorIds, generators]);

  const {
    data: ausfallarbeitData,
    isLoading: isAusfallarbeitLoading,
    error: ausfallarbeitError
  } = useAusfallarbeitTimeSeries(
    numericGeneratorIds,
    dateRange[0].toISO(),
    dateRange[1]?.toISO()
  );

  const chartData = useMemo(() => {
    const data = ausfallarbeitData ?? [];
    const dateMap = new Map<
      string,
      { redispatch: number | null; dva: number | null }
    >();

    for (const series of data) {
      series.index.forEach((dateStr, i) => {
        const existing = dateMap.get(dateStr) ?? {
          redispatch: null,
          dva: null
        };
        const val = series.values[i];
        if (series.abregler === Abregler.netzbetreiber) {
          existing.redispatch =
            existing.redispatch === null
              ? val
              : existing.redispatch + (val ?? 0);
        } else if (series.abregler === Abregler.direktvermarkter) {
          existing.dva =
            existing.dva === null ? val : existing.dva + (val ?? 0);
        }
        dateMap.set(dateStr, existing);
      });
    }

    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, sums]) => ({
        date: DateTime.fromISO(dateStr).toMillis(),
        ...sums
      }));
  }, [ausfallarbeitData]);

  function handleClickDownload() {
    if (!ausfallarbeitData || !generators) return;
    const exportTableData = buildAusfallarbeitExportTableData(
      ausfallarbeitData,
      generators
    );

    const startDate = dateRange[0].toFormat(LUXON_MANTINE_DATEFORMAT);
    const endDate = (dateRange[1] ?? dateRange[0]).toFormat(
      LUXON_MANTINE_DATEFORMAT
    );

    downloadAsExcel(
      exportTableData,
      `ausfallarbeit-zeitreihen-${startDate}-${endDate}`
    );
  }

  const isAllSelected = selectedGeneratorIds.includes(ALL_GENERATORS_VALUE);

  return (
    <Stack gap="md">
      <LoadOrError error={companiesError}>
        {companyOptions.length === 0 && <AusfallSalesCard />}
        <Group align="flex-end" gap="md" wrap="nowrap">
          <Select
            data={companyOptions}
            disabled={isLoadingCompanies}
            label="Unternehmen"
            size="xs"
            value={companyId?.toString() ?? null}
            w={280}
            onChange={(val) => {
              setCompanyId(val ? Number(val) : undefined);
              setSelectedGeneratorIds([ALL_GENERATORS_VALUE]);
            }}
          />

          {isAllSelected ? (
            <Select
              data={generatorOptions}
              disabled={isLoadingGenerators || !generators?.length}
              label="Anlagen (TR-ID)"
              renderOption={(props) =>
                renderGeneratorOption(props, isAllSelected)
              }
              size="xs"
              value={ALL_GENERATORS_VALUE}
              w={280}
              onChange={(val) => {
                if (val && val !== ALL_GENERATORS_VALUE) {
                  setSelectedGeneratorIds([val]);
                }
              }}
            />
          ) : (
            <MultiSelect
              data={generatorOptions}
              disabled={isLoadingGenerators || !generators?.length}
              label="Anlagen (TR-ID)"
              renderOption={(props) =>
                renderGeneratorOption(props, isAllSelected)
              }
              size="xs"
              value={selectedGeneratorIds}
              w={280}
              onChange={(vals) => {
                if (vals.includes(ALL_GENERATORS_VALUE) || vals.length === 0) {
                  setSelectedGeneratorIds([ALL_GENERATORS_VALUE]);
                } else {
                  setSelectedGeneratorIds(vals);
                }
              }}
            />
          )}

          <MonthPickerInput
            allowSingleDateInRange
            label="Berechneter Zeitraum"
            maxDate={MAX_DATE.toJSDate()}
            minDate={MIN_DATE.toJSDate()}
            size="xs"
            type="range"
            value={[dateRange[0].toJSDate(), dateRange[1]?.toJSDate() ?? null]}
            onChange={(dateRange) => {
              const firstDate = DateTime.fromISO(dateRange[0] ?? "");
              const lastDate = dateRange[1]
                ? DateTime.fromISO(dateRange[1]).endOf("month")
                : null;
              if (
                firstDate.isValid &&
                (lastDate === null || lastDate.isValid)
              ) {
                setDateRange([
                  firstDate as DateTime<true>,
                  lastDate as DateTime<true> | null
                ]);
              }
            }}
          />
          <Button
            color="primary"
            disabled={!ausfallarbeitData || ausfallarbeitData.length === 0}
            onClick={handleClickDownload}
          >
            Herunterladen
          </Button>
        </Group>
      </LoadOrError>

      <LoadOrError error={generatorsError || ausfallarbeitError}>
        <AusfallarbeitChart
          data={chartData}
          isLoading={isAusfallarbeitLoading}
        />
        <AusfallarbeitTable
          ausfallarbeitData={ausfallarbeitData}
          generators={generators}
          isLoading={isAusfallarbeitLoading}
        />
      </LoadOrError>
    </Stack>
  );
}

export { AusfallarbeitPage };
